import React, { useMemo, useCallback } from "react";
// import useIdle from "../../hooks/useIdle";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import "firebase/compat/analytics";
import "firebase/compat/remote-config";

import { useRecoilState, useSetRecoilState } from "recoil";
import {
  Box,
  AppBar,
  Typography,
  List,
  ListItem,
  Container,
  Button,
} from "@mui/material";
import Bugsnag from "@bugsnag/js";
import { consola } from "consola";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import BarChartIcon from "@mui/icons-material/BarChart";
import DashboardIcon from "@mui/icons-material/Dashboard";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import { Routes, Route } from "react-router-dom";

import MenuItemComponent from "../../components/MenuItem";
import MainHeader from "../../components/MainHeader";
import ReportPage from "./children/ReportPage";
import DashboardPage from "./children/DashboardPage";
import ClientsPage from "./children/ClientsPage";
import CoachesPage from "./children/CoachesPage";
import MessagesPage from "./children/MessagesPage";
import SharedVideoPage from "./children/SharedVideoPage";
import ExercisesPage from "./children/ExercisesPage";
import RecipesPage from "./children/RecipePage";
import RecipeDetailPage from "./children/RecipeDetailPage";
import MealPlanDetailPage from "./children/MealPlanDetailPage";
import LibraryPage from "./children/LibraryPage";
import AdminPage from "./children/AdminPage";
import DevelopersPage from "./children/DevelopersPage";
import ProtocolsPage from "./children/ProtocolsPage";
import TrainingPlansV2Page from "./children/TrainingPlansV2Page";
import LearnPage from "./children/learn/LearnPage";
import LearnArticle from "./children/learn/LearnArticle";
import CoachProfilePage from "./children/CoachProfilePage";
import FloatingAIChat from "../../components/organisms/chat/FloatingAIChat";

import UserData from "../../data/UserData";
import { pages } from "../../data/GlobalData";
import MessageData from "../../data/MessageData";
import OrganizationData from "../../data/OrganizationData";
import useUsers from "../../hooks/useUsers";

import Emitter from "../../Emitter";

import StillTherePage from "../../components/organisms/StillTherePage";
import NewColors from "../../themes/NewColors";

import userDataState from "../../atoms/userDataAtom";
import organizationDataState from "../../atoms/organizationDataAtom";
import organizationIdState from "../../atoms/organizationIdAtom";
import difficultyKeysState from "../../atoms/difficultyKeysAtom";
import { useNavigate, useLocation } from "react-router-dom";
import useRenderCount from "../../hooks/useRenderCount";
import useCoachSignOut from "../../hooks/useCoachSignOut";
import useClientsAggregate from "../../hooks/useClientsAggregate";
import useReportHistory from "../../hooks/useReportHistory";
import useRecipes from "../../hooks/useRecipes";
import useClientsLastSeen from "../../hooks/useClientsLastSeen";
import useProtocolsSubscription from "../../hooks/useProtocolsSubscription";
import { useDifficultyBigQuery } from "../../hooks/useStaticDataBigQuery";
import { isTrainingPlansV2Enabled } from "./children/training-plans-v2/services/trainingPlansV2Service";
import useEntitlements from "../../hooks/useEntitlements";
import UpgradePrompt from "../../components/organisms/UpgradePrompt";
// import Footer from "./Footer";

const SIDEBAR_EXPANDED_WIDTH = 176;
const SIDEBAR_COLLAPSED_WIDTH = 64;

const HomePage = () => {
  useRenderCount({ name: "HomePage" });

  const location = useLocation();
  const navigate = useNavigate();

  useUsers();

  const savedPage = localStorage.getItem("lastPage");

  const setOrganizationIdState = useSetRecoilState(organizationIdState);

  // Exercise state is now managed by ExercisesBigQueryBridge component
  const setDifficultyKeysState = useSetRecoilState(difficultyKeysState);
  const [userData, setUserData] = useRecoilState(userDataState);
  const [organizationData, setOrganizationData] = useRecoilState(
    organizationDataState,
  );
  const [isUserDataLoading, setIsUserDataLoading] = React.useState(true);

  useClientsAggregate();
  useReportHistory();
  useProtocolsSubscription();

  const organizationId = localStorage.getItem("organizationId");

  const { getMealPlanById } = useRecipes({ organizationId });
  const { lastSeenUsers } = useClientsLastSeen({ organizationId });
  const { hasFeature, organizationData: entitlementsOrgData } = useEntitlements();
  const canAccessApi = hasFeature("api_access");

  // Use BigQuery hook for difficulty data instead of Firebase
  const { data: difficultyData, isSuccess: difficultyLoaded } =
    useDifficultyBigQuery();

  const [menu, setMenu] = React.useState(
    savedPage ? savedPage : pages.dashboard,
  );

  // Map routes to menu states (memoized to prevent re-renders)
  const routeToMenuMap = useMemo(
    () => ({
      "/home/dashboard": pages.dashboard,
      "/home/reports": pages.reports,
      "/home/clients": pages.clients,
      "/home/coaches": pages.coaches,
      "/home/messages": pages.messages,
      "/home/shared-videos": pages.shared_videos,
      "/home/protocols": pages.protocols,
      "/home/training-plans-v2": pages.training_plans_v2,
      "/home/exercises": pages.exercises,
      "/home/recipes": pages.recipes,
      "/home/meal-plans": pages.recipes,
      "/home/supplements": pages.recipes,
      "/home/ingredients": pages.recipes,
      "/home/library": pages.library,
      "/home/learn": pages.learn,
      "/home/admin": pages.admin,
      // Keep Admin highlighted when viewing API/integrations (no sidebar item)
      "/home/developers": pages.admin,
    }),
    [],
  );

  React.useEffect(() => {
    const organizationId = localStorage.getItem("organizationId");
    if (organizationId) {
      setOrganizationIdState(organizationId);
      
      // Immediately set up listener when organizationId is available
      // This ensures data is loaded as soon as possible, even before the main data fetch completes
      const orgRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId);
      
      // Get initial data immediately
      orgRef.get().then((doc) => {
        if (doc.exists) {
          const initialData = {
            ...doc.data(),
            id: doc.id,
          };
          setOrganizationData(initialData);
          consola.debug("++++++ READ -> HomePage: initial organization data loaded");
        }
      }).catch((error) => {
        consola.error("Error loading initial organization data:", error);
      });
    }
  }, [setOrganizationIdState]);

  React.useEffect(() => {
    if (location.pathname === "/home") {
      navigate("/home/dashboard", { replace: true });
    } else {
      const currentPath = location.pathname;
      let currentMenu = routeToMenuMap[currentPath];
      if (!currentMenu) {
        if (currentPath.startsWith("/home/protocols")) {
          currentMenu = pages.protocols;
        } else if (currentPath.startsWith("/home/training-plans-v2")) {
          currentMenu = pages.training_plans_v2;
        } else if (currentPath.startsWith("/home/shared-videos")) {
          currentMenu = pages.shared_videos;
        }
      }
      if (currentMenu && currentMenu !== menu) {
        setMenu(currentMenu);
      }
    }
  }, [location.pathname, routeToMenuMap, menu, navigate]);

  const menuStateStore = localStorage.getItem("menuState");
  const [menuState, setMenuState] = React.useState(
    menuStateStore ? menuStateStore : "expanded",
  );
  const isMenuExpanded = menuState === "expanded";

  const [update, setUpdate] = React.useState(new Date());
  const handleSignOut = useCoachSignOut();

  const sessionTimerManualLogout = useCallback(() => {
    handleSignOut("Manual From Session Timeout");
  }, [handleSignOut]);

  const sessionTimerForcedLogout = useCallback(() => {
    handleSignOut("Session Timed Out");
  }, [handleSignOut]);

  React.useEffect(() => {
    const getData = async () => {
      try {
        setIsUserDataLoading(true);
        const uid = localStorage.getItem("uid");
        const userData = await UserData.getUserData(uid);

        if (
          !userData ||
          !firebase.auth().currentUser ||
          firebase.auth().currentUser.uid !== userData.authId
        ) {
          localStorage.removeItem("uid");
          localStorage.removeItem("email");

          await firebase.auth().signOut();
          navigate("/");
          return;
        }

        const organizationData = await OrganizationData.getOrganization(uid);
        setUserData(userData);
        setOrganizationData(organizationData);

        localStorage.setItem("organizationId", organizationData.id);
        setOrganizationIdState(organizationData.id);

        // Exercise data is now managed by ExercisesBigQueryBridge component
        // Difficulty data is now loaded via BigQuery hook

        if (
          userData.role !== "admin" &&
          (savedPage === pages.admin || savedPage === pages.developers)
        ) {
          localStorage.setItem("lastPage", pages.reports);
          setMenu(pages.reports);
        }
      } catch (error) {
        Bugsnag.notify(error);

        localStorage.removeItem("uid");
        localStorage.removeItem("organizationId");
        localStorage.removeItem("email");
        await firebase.auth().signOut();
        navigate("/signin");
      } finally {
        setIsUserDataLoading(false);
      }
    };

    getData();
  }, [
    update,
    navigate,
    setUserData,
    setOrganizationData,
    setOrganizationIdState,
    savedPage,
  ]);

  // Set up real-time listener for organization data updates
  // This ensures the organization data stays in sync when navigating between pages
  // This listener runs immediately when organizationId is available, even if organizationData hasn't loaded yet
  React.useEffect(() => {
    if (!organizationId) {
      return;
    }

    const orgRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId);

    // Set up listener immediately - this will update data as soon as it's available
    const unsubscribe = orgRef.onSnapshot(
      (doc) => {
        if (doc.exists) {
          const updatedData = {
            ...doc.data(),
            id: doc.id,
          };
          setOrganizationData(updatedData);
          consola.debug("++++++ SNAPSHOT -> HomePage: organization updated");
        }
      },
      (error) => {
        consola.error("Error listening to organization updates:", error);
        Bugsnag.notify(error);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [organizationId, setOrganizationData]);

  // Refresh organization data when navigating back to this page
  // This ensures data is fresh after returning from other pages like /subscribe
  React.useEffect(() => {
    const refreshOrgData = async () => {
      const uid = localStorage.getItem("uid");
      if (uid && organizationId) {
        try {
          const updatedOrgData = await OrganizationData.getOrganization(uid);
          if (updatedOrgData) {
            setOrganizationData(updatedOrgData);
            consola.debug("++++++ REFRESH -> HomePage: organization data refreshed");
          }
        } catch (error) {
          consola.error("Error refreshing organization data:", error);
        }
      }
    };

    // Refresh when location pathname includes /home (user navigated back to home)
    if (location.pathname.startsWith("/home")) {
      refreshOrgData();
    }
  }, [location.pathname, organizationId, setOrganizationData]);

  // Handle difficulty data from BigQuery
  React.useEffect(() => {
    if (difficultyLoaded && difficultyData) {
      // Transform BigQuery data to match Firebase format for compatibility
      const difficultyKeys = {};
      difficultyData.forEach((item) => {
        difficultyKeys[item.id] = item.name;
      });
      setDifficultyKeysState(difficultyKeys);
    }
  }, [difficultyLoaded, difficultyData, setDifficultyKeysState]);

  React.useEffect(() => {
    if (
      userData &&
      userData.role !== "admin" &&
      (savedPage === pages.admin || savedPage === pages.developers)
    ) {
      localStorage.setItem("lastPage", pages.reports);
      setMenu(pages.reports);
    }
  }, [userData, savedPage]);

  // Custom exercise subscription is now handled by ExercisesBigQueryBridge and React Query

  React.useEffect(() => {
    const organizationId = localStorage.getItem("organizationId");
    const uid = localStorage.getItem("uid");

    let subscription = null;
    let orgChangeListener = null;

    if (organizationId && uid) {
      // subscribe to messageHistory
      subscription = MessageData.getMessagesHistorySubscription({
        uid,
        onMessages: async (messages) => {
          if (messages && messages.length > 0) {
            if (!("Notification" in window)) {
              return;
            }

            const lastMessage = messages[0].data();

            const user = `Mighty45 - ${lastMessage.message.fromUser.firstName} ${lastMessage.message.fromUser.lastName}`;

            if (Notification.permission === "granted") {
              const notification = new Notification(user, {
                body: lastMessage.message.message,
                icon: lastMessage.message.fromUser.avatarUrl,
              });

              notification.onclick = (event) => {
                event.preventDefault(); // prevent the browser from focusing the Notification's tab
                window.focus();
                notification.close();
              };
            }

            await MessageData.markMessageHistorySent({
              messageId: lastMessage.id,
              uid: uid,
            });
          }
        },
      });
    }

    // Org refresh without re-running full bootstrap (avoids isUserDataLoading + whole-route flash)
    const handleOrgChange = async () => {
      const uid = localStorage.getItem("uid");
      const orgId = localStorage.getItem("organizationId");
      if (!uid || !orgId) return;
      try {
        const updatedOrgData = await OrganizationData.getOrganization(uid);
        if (updatedOrgData) {
          setOrganizationData(updatedOrgData);
        }
      } catch (error) {
        Bugsnag.notify(error);
      }
    };

    Emitter.removeListener("orgChanged");
    Emitter.on("orgChanged", handleOrgChange);
    orgChangeListener = () => {
      Emitter.removeListener("orgChanged", handleOrgChange);
    };

    return () => {
      if (subscription) {
        subscription();
      }
      if (orgChangeListener) {
        orgChangeListener();
      }
    };
  }, [setOrganizationData]);

  // function handleHyperlink(title, link) {
  //   const linkEvent = { objectName: title, hyperlink: link };
  //   events.logHyperlink(linkEvent);
  // }

  // Removed showProgress function as it's now in MainHeader component

  const toggleMenuState = useCallback(() => {
    const nextMenuState = menuState === "expanded" ? "collapsed" : "expanded";
    setMenuState(nextMenuState);
    localStorage.setItem("menuState", nextMenuState);
  }, [menuState]);

  // Memoized navigation handlers to prevent re-renders
  const handleDashboardClick = useCallback(() => {
    navigate("/home/dashboard");
    setMenu(pages.dashboard);
  }, [navigate]);

  const handleReportsClick = useCallback(() => {
    navigate("/home/reports");
    setMenu(pages.reports);
  }, [navigate]);

  const handleClientsClick = useCallback(() => {
    navigate("/home/clients");
    setMenu(pages.clients);
  }, [navigate]);

  const handleCoachesClick = useCallback(() => {
    navigate("/home/coaches");
    setMenu(pages.coaches);
  }, [navigate]);

  const handleMessagesClick = useCallback(() => {
    navigate("/home/messages");
    setMenu(pages.messages);
  }, [navigate]);

  const handleSharedVideosClick = useCallback(() => {
    navigate("/home/shared-videos");
    setMenu(pages.shared_videos);
  }, [navigate]);

  const handleTrainingPlansV2Click = useCallback(() => {
    navigate("/home/training-plans-v2");
    setMenu(pages.training_plans_v2);
  }, [navigate]);

  const handleExercisesClick = useCallback(() => {
    navigate("/home/exercises");
    setMenu(pages.exercises);
  }, [navigate]);

  const handleRecipesClick = useCallback(() => {
    navigate("/home/recipes");
    setMenu(pages.recipes);
  }, [navigate]);

  const handleLibraryClick = useCallback(() => {
    navigate("/home/library");
    setMenu(pages.library);
  }, [navigate]);

  const handleLearnClick = useCallback(() => {
    navigate("/home/learn");
    setMenu(pages.learn);
  }, [navigate]);

  const handleAdminClick = useCallback(() => {
    navigate("/home/admin");
    setMenu(pages.admin);
  }, [navigate]);

  // Memoized props to prevent re-renders
  const clientsPageProps = useMemo(
    () => ({
      additionalInfo: { getMealPlanById, lastSeenUsers },
    }),
    [getMealPlanById, lastSeenUsers],
  );

  // Check if upgrade prompt should be shown (same logic as MainHeader)
  const [upgradeBannerDismissed, setUpgradeBannerDismissed] = React.useState(
    () => localStorage.getItem("upgradeBannerDismissed") === "true"
  );
  const showTrainingPlansV2 = isTrainingPlansV2Enabled();
  const shouldShowUpgradePrompt =
    !upgradeBannerDismissed &&
    organizationData &&
    (organizationData.account?.accountType === "free" ||
      !organizationData.account?.accountType ||
      (organizationData.account?.stripeCustomerId &&
        !organizationData.account?.stripeSubscriptionId &&
        organizationData.account?.accountType !== "standard" &&
        organizationData.account?.accountType !== "pro" &&
        organizationData.account?.accountType !== "special_pricing"));

  const handleDismissBanner = () => {
    setUpgradeBannerDismissed(true);
    localStorage.setItem("upgradeBannerDismissed", "true");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <AppBar
        elevation={0}
        position="static"
        style={{ backgroundColor: "white" }}
      >
          {/* {accountData.expired && <SubscribeNowDialog />} */}

          <StillTherePage
            onManualLogout={sessionTimerManualLogout}
            onForcedLogout={sessionTimerForcedLogout}
          />

          <MainHeader onUpdate={setUpdate} />
        </AppBar>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flex: 1,
            backgroundColor: "white",
            overflow: "hidden", // Prevent scrolling at this level
          }}
          data-testid="main-container"
        >
        <Box
          boxShadow={0}
          sx={{
            width: isMenuExpanded
              ? SIDEBAR_EXPANDED_WIDTH
              : SIDEBAR_COLLAPSED_WIDTH,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            flexShrink: 0,
            transition: "width 180ms ease",
          }}
          data-testid="sidebar"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: isMenuExpanded ? "flex-end" : "center",
              px: isMenuExpanded ? 1 : 0,
              py: 0.75,
            }}
          >
            <IconButton
              size="small"
              onClick={toggleMenuState}
              aria-label={isMenuExpanded ? "Collapse sidebar" : "Expand sidebar"}
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "white",
                "&:hover": {
                  backgroundColor: NewColors.sectionBackground,
                },
              }}
            >
              {isMenuExpanded ? (
                <ArrowLeftIcon sx={{ fontSize: 20 }} />
              ) : (
                <ArrowRightIcon sx={{ fontSize: 20 }} />
              )}
            </IconButton>
          </Box>

          <List
            sx={{
              flex: 1,
              overflow: "auto",
              pt: 0.5,
              px: isMenuExpanded ? 1 : 0.5,
              "& .MuiListItem-root": {
                justifyContent: isMenuExpanded ? "stretch" : "center",
                px: 0,
                // Collapsed: match ~row height of expanded MenuButtons (margin + button) so icon spacing isn’t cramped
                py: isMenuExpanded ? 0.25 : 1.25,
              },
            }}
          >
            <ListItem>
              <MenuItemComponent
                id={pages.dashboard}
                title="Dashboard"
                menuState={menuState}
                iconComponent={DashboardIcon}
                enabled={menu === pages.dashboard}
                menuClicked={handleDashboardClick}
              />
            </ListItem>
            <ListItem>
              <MenuItemComponent
                id={pages.reports}
                title="Reports"
                menuState={menuState}
                iconComponent={BarChartIcon}
                enabled={menu === pages.reports}
                menuClicked={handleReportsClick}
              />
            </ListItem>
            <ListItem>
              <MenuItemComponent
                id={pages.clients}
                title="Clients"
                menuState={menuState}
                enabled={menu === pages.clients}
                menuClicked={handleClientsClick}
              />
            </ListItem>
            <ListItem>
              <MenuItemComponent
                id={pages.coaches}
                title="Coaches"
                menuState={menuState}
                enabled={menu === pages.coaches}
                menuClicked={handleCoachesClick}
              />
            </ListItem>
            <ListItem>
              <MenuItemComponent
                id={pages.messages}
                title="Messages"
                menuState={menuState}
                enabled={menu === pages.messages}
                menuClicked={handleMessagesClick}
              />
            </ListItem>
            <ListItem>
              <MenuItemComponent
                id={pages.shared_videos}
                title="Media"
                menuState={menuState}
                enabled={menu === pages.shared_videos}
                menuClicked={handleSharedVideosClick}
              />
            </ListItem>
            {showTrainingPlansV2 && (
              <ListItem>
                <MenuItemComponent
                  id={pages.training_plans_v2}
                  title="Training Plans"
                  menuState={menuState}
                  enabled={menu === pages.training_plans_v2}
                  menuClicked={handleTrainingPlansV2Click}
                />
              </ListItem>
            )}
            <ListItem>
              <MenuItemComponent
                id={pages.recipes}
                title="Nutrition"
                menuState={menuState}
                enabled={menu === pages.recipes}
                menuClicked={handleRecipesClick}
              />
            </ListItem>
            <ListItem>
              <MenuItemComponent
                id={pages.exercises}
                title="Exercise DB"
                menuState={menuState}
                enabled={menu === pages.exercises}
                menuClicked={handleExercisesClick}
              />
            </ListItem>
            
            <ListItem>
              <MenuItemComponent
                id={pages.library}
                title="Library"
                menuState={menuState}
                enabled={menu === pages.library}
                menuClicked={handleLibraryClick}
              />
            </ListItem>

            <ListItem>
              <MenuItemComponent
                id={pages.learn}
                title="Help"
                menuState={menuState}
                enabled={menu === pages.learn}
                menuClicked={handleLearnClick}
              />
            </ListItem>

            {userData && userData.role === "admin" && (
              <ListItem>
                <MenuItemComponent
                  id={pages.admin}
                  title="Admin"
                  menuState={menuState}
                  enabled={menu === pages.admin}
                  menuClicked={handleAdminClick}
                />
              </ListItem>
            )}
            {/* Upgrade prompt bubble right under Admin menu */}
            {shouldShowUpgradePrompt && menuState === "expanded" && (
              <ListItem sx={{ display: "block", px: 1.5, py: 1 }}>
                <Box
                  sx={{
                    backgroundColor: NewColors.brand.primary,
                    color: "white",
                    borderRadius: 2,
                    p: 1.5,
                    position: "relative",
                    boxShadow: 3,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={handleDismissBanner}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      display: "block",
                      mb: 0.5,
                      pr: 3, // Space for close button
                      fontSize: "0.75rem",
                    }}
                  >
                    Unlock Premium
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.65rem",
                      opacity: 0.9,
                      display: "block",
                      mb: 1,
                      pr: 3,
                      lineHeight: 1.3,
                    }}
                  >
                    Upgrade to Standard or Pro for meal plans, AI features, and more
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => navigate("/subscribe")}
                    fullWidth
                    sx={{
                      backgroundColor: "white",
                      color: NewColors.brand.primary,
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      textTransform: "none",
                      py: 0.5,
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                      },
                    }}
                  >
                    Upgrade Now
                  </Button>
                </Box>
              </ListItem>
            )}
          </List>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            backgroundColor: "#F6F7FB",
            minWidth: 0,
            overflow: "hidden", // Prevent scrolling at content area level
          }}
          data-testid="content-area"
        >
          <Container
            maxWidth={false}
            disableGutters
            sx={{
              flex: 1, // Take remaining space after banner
              minHeight: 0, // Important for flex children
              pb: 0,
              mb: 0,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              overflow: "hidden", // Prevent scrolling at Container level
              // Removed overflow: auto - pages handle their own scrolling
              // Removed mt: 2 - spacing handled by PageContent
              // disableGutters: main column headers align with sidebar; page bodies use px via PageContent or per-page Box
            }}
          >
            <Routes>
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="reports/*" element={<ReportPage />} />
              <Route
                path="clients/*"
                element={<ClientsPage {...clientsPageProps} />}
              />
              <Route path="coaches/*" element={<CoachesPage />} />
              <Route path="messages" element={<MessagesPage />} />
              <Route path="shared-videos/*" element={<SharedVideoPage />} />
              <Route path="protocols/*" element={<ProtocolsPage />} />
              <Route path="training-plans-v2/*" element={<TrainingPlansV2Page />} />
              <Route path="exercises/*" element={<ExercisesPage />} />
              <Route path="recipes" element={<RecipesPage />} />
              <Route
                path="meal-plans"
                element={<RecipesPage initialTab="meal-plans" />}
              />
              <Route
                path="supplements"
                element={<RecipesPage initialTab="supplements" />}
              />
              <Route
                path="ingredients"
                element={<RecipesPage initialTab="ingredients" />}
              />
              <Route
                path="recipes/recipe/:orgId/:recipeId"
                element={<RecipeDetailPage />}
              />
              <Route
                path="recipes/mealplan/:orgId/:mealPlanId"
                element={<MealPlanDetailPage />}
              />
              <Route path="library" element={<LibraryPage />} />
              <Route path="learn" element={<LearnPage />} />
              <Route path="learn/:slug" element={<LearnArticle />} />
              <Route path="profile" element={<CoachProfilePage />} />
              <Route
                path="admin"
                element={
                  isUserDataLoading ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="400px"
                    >
                      <Typography>Loading...</Typography>
                    </Box>
                  ) : userData?.role === "admin" ? (
                    <AdminPage />
                  ) : (
                    <Typography>
                      Access denied. Admin privileges required.
                    </Typography>
                  )
                }
              />
              <Route
                path="developers"
                element={
                  isUserDataLoading ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="400px"
                    >
                      <Typography>Loading...</Typography>
                    </Box>
                  ) : userData?.role !== "admin" ? (
                    <Typography>
                      Access denied. Admin privileges required.
                    </Typography>
                  ) : !canAccessApi ? (
                    <Box sx={{ p: 3, maxWidth: 560 }}>
                      <UpgradePrompt
                        organizationData={entitlementsOrgData || organizationData}
                        minTier="pro"
                        title="Upgrade for API access"
                        description="Organization API keys and developer integrations are available on Pro."
                        featureName="api_access"
                      />
                    </Box>
                  ) : (
                    <DevelopersPage />
                  )
                }
              />
              <Route path="" element={<DashboardPage />} />
              <Route
                path="*"
                element={<Typography>Page not found</Typography>}
              />
            </Routes>
          </Container>
          {/* <Footer
            handleSignOut={handleSignOut}
            sx={{ mt: "auto" }}
          /> */}
        </Box>
      </Box>
      </Box>
      <FloatingAIChat />
    </Box>
  );
};

export default HomePage;
